/* Anagen Care — core interactions (header, drawer, announcements, cards, rails).
   No dependencies. Loaded with `defer` on every page. */
(() => {
  const root = document.documentElement;
  const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const Anagen = (window.Anagen = window.Anagen || {});
  Anagen.reducedMotion = () => mqReduce.matches || root.classList.contains('an-motion-off');
  Anagen.isRTL = () => root.dir === 'rtl';

  /* ---------------------------------------------------------------- utils */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* --------------------------------------------------------------- header */
  class AnHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.mode = this.dataset.sticky || 'reveal';
      this.lastY = window.scrollY;
      this.openPanel = null;
      this.closeTimer = null;

      // sticky comes from CSS; 'none' opts out
      this.section.classList.toggle('an-no-sticky', this.mode === 'none');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      this.setupMega();
      this.setupDrawer();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.drawer && this.drawer.parentElement === document.body) this.drawer.remove();
      root.classList.remove('an-lock');
    }

    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-scrolled', y > 4);
      if (this.mode === 'reveal') {
        const goingDown = y > this.lastY;
        const hide = goingDown && y > 240 && !this.openPanel && !this.drawerOpen;
        this.section.classList.toggle('an-is-hidden', hide);
      }
      this.lastY = y;
      root.style.setProperty(
        '--an-header-offset',
        this.section.classList.contains('an-is-hidden') || this.mode === 'none' ? '0px' : `${this.offsetHeight}px`
      );
    }

    /* mega menu: hover intent on pointer devices, click/keyboard everywhere */
    setupMega() {
      this.triggers = [...this.querySelectorAll('[data-mega-trigger]')];
      const canHover = window.matchMedia('(hover: hover)').matches;

      this.triggers.forEach((trigger) => {
        const panel = this.querySelector(`#${trigger.getAttribute('aria-controls')}`);
        if (!panel) return;

        trigger.addEventListener('click', () => {
          this.openPanel === panel ? this.closeMega() : this.openMega(trigger, panel);
        });

        if (canHover) {
          const item = trigger.closest('.an-nav__item');
          item.addEventListener('mouseenter', () => {
            clearTimeout(this.closeTimer);
            this.openMega(trigger, panel);
          });
          item.addEventListener('mouseleave', () => this.scheduleClose());
          panel.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
          panel.addEventListener('mouseleave', () => this.scheduleClose());
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.openPanel) {
          const trigger = this.openTrigger;
          this.closeMega();
          trigger && trigger.focus();
        }
      });

      document.addEventListener('click', (e) => {
        if (this.openPanel && !this.contains(e.target)) this.closeMega();
      });

      // close when focus leaves the open panel and its trigger
      this.addEventListener('focusout', (e) => {
        if (!this.openPanel) return;
        const next = e.relatedTarget;
        if (next && (this.openPanel.contains(next) || next === this.openTrigger)) return;
        if (next && next.hasAttribute && next.hasAttribute('data-mega-trigger')) return;
        this.closeMega();
      });
    }

    scheduleClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeMega(), 180);
    }

    openMega(trigger, panel) {
      if (this.openPanel === panel) return;
      this.closeMega(true);
      panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      this.openPanel = panel;
      this.openTrigger = trigger;
    }

    closeMega() {
      if (!this.openPanel) return;
      this.openPanel.classList.remove('is-open');
      this.openTrigger && this.openTrigger.setAttribute('aria-expanded', 'false');
      this.openPanel = null;
      this.openTrigger = null;
    }

    /* drawer */
    setupDrawer() {
      this.drawer = this.querySelector('[data-drawer]');
      if (!this.drawer) return;
      // move the drawer to <body> so the sticky/transform header never traps it
      document.body.appendChild(this.drawer);
      this.drawerPanel = this.drawer.querySelector('[role="dialog"]');
      this.openBtn = this.querySelector('[data-drawer-open]');

      this.openBtn && this.openBtn.addEventListener('click', () => this.openDrawer());
      this.drawer.querySelectorAll('[data-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeDrawer())
      );
      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeDrawer();
        trapFocus(this.drawerPanel, e);
      });

      this.drawer.querySelectorAll('[data-drawer-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = this.drawer.querySelector(`#${btn.getAttribute('aria-controls')}`);
          const open = btn.getAttribute('aria-expanded') !== 'true';
          btn.setAttribute('aria-expanded', String(open));
          sub && sub.classList.toggle('is-open', open);
        });
      });

      window.matchMedia('(min-width: 1100px)').addEventListener('change', (e) => {
        if (e.matches) this.closeDrawer();
      });
    }

    openDrawer() {
      this.drawerOpen = true;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.openBtn && this.openBtn.setAttribute('aria-expanded', 'true');
      root.classList.add('an-lock');
      requestAnimationFrame(() => this.drawerPanel.focus({ preventScroll: true }));
    }

    closeDrawer() {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.openBtn && this.openBtn.setAttribute('aria-expanded', 'false');
      root.classList.remove('an-lock');
      this.openBtn && this.openBtn.focus({ preventScroll: true });
    }
  }
  if (!customElements.get('an-header')) customElements.define('an-header', AnHeader);

  /* -------------------------------------------------------- announcements */
  class AnAnnounce extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.an-announce__item')];
      this.index = 0;
      this.interval = (parseInt(this.dataset.interval, 10) || 5) * 1000;
      this.querySelectorAll('[data-dir]').forEach((btn) =>
        btn.addEventListener('click', () => {
          this.go(this.index + parseInt(btn.dataset.dir, 10));
          this.restart();
        })
      );
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.start();
    }
    disconnectedCallback() {
      this.stop();
    }
    go(i) {
      if (!this.items.length) return;
      this.items[this.index].classList.remove('is-active');
      this.index = (i + this.items.length) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
    start() {
      if (this.items.length < 2 || Anagen.reducedMotion()) return;
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }
    stop() {
      clearInterval(this.timer);
    }
    restart() {
      this.stop();
      this.start();
    }
  }
  if (!customElements.get('an-announce')) customElements.define('an-announce', AnAnnounce);

  /* ------------------------------------------- quick add: "Added" feedback */
  function bindCartFeedback() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const id = event && event.productVariantId;
      if (!id) return;
      document.querySelectorAll(`.an-quick__btn[data-variant-id="${id}"]`).forEach((btn) => {
        const label = btn.querySelector('.an-quick__label');
        const icon = btn.querySelector('.an-quick__icon');
        btn.classList.add('is-added');
        if (label && label.dataset.labelAdded) label.textContent = label.dataset.labelAdded;
        if (icon) icon.dataset.prev = icon.innerHTML;
        if (icon)
          icon.innerHTML =
            '<svg class="an-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
        clearTimeout(btn._anTimer);
        btn._anTimer = setTimeout(() => {
          btn.classList.remove('is-added');
          if (label && label.dataset.labelDefault) label.textContent = label.dataset.labelDefault;
          if (icon && icon.dataset.prev) icon.innerHTML = icon.dataset.prev;
        }, 2600);
      });
    });
  }

  /* ---------------------------------------------- rails with progress bar */
  function bindRails(scope = document) {
    scope.querySelectorAll('[data-an-rail]').forEach((rail) => {
      if (rail._anRail) return;
      rail._anRail = true;
      const bar = rail.parentElement.querySelector('.an-rail-progress span');
      if (!bar) return;
      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        const ratio = rail.clientWidth / rail.scrollWidth;
        const p = max > 0 ? Math.abs(rail.scrollLeft) / max : 0;
        bar.style.setProperty('--p-w', `${Math.min(100, ratio * 100)}%`);
        bar.style.setProperty('--p-x', `${p * (1 / Math.max(ratio, 0.0001) - 1) * 100}%`);
        bar.parentElement.hidden = max <= 1;
      };
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ------------------------------------------------------- image reveals */
  function bindReveals(scope = document) {
    const els = scope.querySelectorAll('.an-reveal-mask:not(.is-in)');
    if (!els.length) return;
    if (Anagen.reducedMotion() || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px' }
    );
    els.forEach((el) => io.observe(el));
  }

  Anagen.bindRails = bindRails;
  Anagen.bindReveals = bindReveals;

  function init() {
    bindCartFeedback();
    bindRails();
    bindReveals();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (e) => {
    bindRails(e.target);
    bindReveals(e.target);
  });
})();
